import React, { createContext, useRef, useContext, useCallback, ReactNode } from 'react';
export interface ImportSelections {
    alignPeriodOption: string;
    alignPeriodMap: Map<string, string>;
    tagAlias: Map<string, string>;
    athleteAssignment: Map<string, string>;
    athleteAssignmentOption: string;
}

interface FocusImportSelectionsContextValue {
    selectionsRef: React.MutableRefObject<ImportSelections>;
    setAlignPeriodOption: (option: string) => void;
    setAlignPeriodMap: (map: Map<string, string>) => void;
    setTagAlias: (map: Map<string, string>) => void;
    addAlignPeriodMapEntry: (key: string, value: string) => void;
    addTagAliasEntry: (key: string, value: string) => void;
    deleteAlignPeriodMapEntry: (key: string) => void;
    deleteTagAliasEntry: (key: string) => void;
    setAthleteAssignment: (map: Map<string, string>) => void;
    addAthleteAssignmentEntry: (key: string, value: string) => void;
    deleteAthleteAssignmentEntry: (key: string) => void;
    setAthleteAssignmentOption: (option: string) => void;
}

const FocusImportSelectionsContext = createContext<FocusImportSelectionsContextValue | null>(null);

interface FocusImportSelectionsProviderProps {
    children: ReactNode;
}

export const FocusImportSelectionsProvider: React.FC<FocusImportSelectionsProviderProps> = ({ children }) => {
    const selectionsRef = useRef<ImportSelections>({
        alignPeriodOption: '',
        alignPeriodMap: new Map(),
        tagAlias: new Map(),
        athleteAssignment: new Map(),
        athleteAssignmentOption: '',
    });

    const setAlignPeriodOption = useCallback((option: string) => {
        selectionsRef.current.alignPeriodOption = option;
    }, []);

    const setAlignPeriodMap = useCallback((map: Map<string, string>) => {
        selectionsRef.current.alignPeriodMap = map;
    }, []);

    const setTagAlias = useCallback((map: Map<string, string>) => {
        selectionsRef.current.tagAlias = map;
    }, []);

    const addAlignPeriodMapEntry = useCallback((key: string, value: string) => {
        selectionsRef.current.alignPeriodMap.set(key, value);
    }, []);

    const addTagAliasEntry = useCallback((key: string, value: string) => {
        selectionsRef.current.tagAlias.set(key, value);
    }, []);

    const deleteAlignPeriodMapEntry = useCallback((key: string) => {
        selectionsRef.current.alignPeriodMap.delete(key);
    }, []);

    const deleteTagAliasEntry = useCallback((key: string) => {
        selectionsRef.current.tagAlias.delete(key);
    }, []);

    const setAthleteAssignment = useCallback((map: Map<string, string>) => {
        selectionsRef.current.athleteAssignment = map;
    }, []);

    const addAthleteAssignmentEntry = useCallback((key: string, value: string) => {
        selectionsRef.current.athleteAssignment.set(key, value);
    }, []);

    const deleteAthleteAssignmentEntry = useCallback((key: string) => {
        selectionsRef.current.athleteAssignment.delete(key);
    }, []);

    const setAthleteAssignmentOption = useCallback((option: string) => {
        selectionsRef.current.athleteAssignmentOption = option;
    }, []);

    return (
        <FocusImportSelectionsContext.Provider
            value={{
                selectionsRef,
                setAlignPeriodOption,
                setAlignPeriodMap,
                setTagAlias,
                addAlignPeriodMapEntry,
                addTagAliasEntry,
                deleteAlignPeriodMapEntry,
                deleteTagAliasEntry,
                setAthleteAssignment,
                addAthleteAssignmentEntry,
                deleteAthleteAssignmentEntry,
                setAthleteAssignmentOption,
            }}
        >
            {children}
        </FocusImportSelectionsContext.Provider>
    );
};

export const useFocusImportSelections = (): FocusImportSelectionsContextValue => {
    const context = useContext(FocusImportSelectionsContext);
    if (!context) {
        throw new Error('useFocusImportSelections must be used within a FocusImportSelectionsProvider');
    }
    return context;
};
